import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Link } from 'react-router-dom';

import { usersApi } from '@/api/users';
import { PageHeader } from '@/components/ui/PageHeader';
import { SearchInput } from '@/components/ui/SearchInput';
import { curatorStudentsLabel, filterCurators } from '@/lib/curatorList';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { humanizeError } from '@/lib/errors';
import { MAdd, MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { StudentFormModal } from './StudentFormModal';

export function AdminCuratorsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  // Status labels built with t() to keep them reactive to language changes.
  const STATUS_LABEL: Record<string, string> = {
    active: t('adminStudents.statusActive'),
    deactivated: t('adminStudents.statusDeactivated'),
    blocked: t('adminStudents.statusBlocked'),
    deleted: t('adminStudents.statusDeleted'),
  };
  const [creatingOpen, setCreatingOpen] = useState(false);

  // ⚠️ ОДИН ЗАПРОС НА СТРАНИЦУ (28.09.2026, вторая волна п. 9). Раньше к списку добавлялись два запроса на КАЖДОГО
  // куратора — «сколько учеников» и подтипы; теперь сервер кладёт в строку куратора `curator_students`,
  // `curator_subtypes` и `curator_groups`. limit 200: кураторов меньше, листать не нужно — ищут поиском.
  const list = useQuery({
    queryKey: ['admin', 'curators'],
    queryFn: () => usersApi.list({ role: 'curator', limit: 200 }),
  });

  const all = list.data?.items ?? [];
  const [q, setQ] = useState('');
  const curators = filterCurators(all, q);
  const modal = (
    <StudentFormModal open={creatingOpen} defaultRole="curator" onClose={() => setCreatingOpen(false)} onSaved={() => qc.invalidateQueries({ queryKey: ['admin', 'curators'] })} />
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.12) ─────────────────────────
  // «+» в шапке вместо кнопки, строки «имя · N учеников · подтипы» одним блоком; строка ведёт в карточку куратора. Без
  // подтипов строка оранжевая: такой куратор не видит ни курсов, ни учеников (D40).
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('adminCurators.title')}
          subtitle={t('adminCurators.totalCount', { count: list.data?.total ?? 0 })}
          right={<MAdd label={t('adminStudents.add')} onClick={() => setCreatingOpen(true)} />}
        />
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !all.length ? (
          <MEmpty icon="group" title={t('adminCurators.empty')} />
        ) : (
          <>
            <SearchInput value={q} placeholder={t('adminCurators.searchPlaceholder')} onSearch={setQ} />
            <p className="px-0.5 text-[12px] text-on-surface-variant">
              {t('common.shownOf', { shown: curators.length, total: all.length })}
            </p>
            {!curators.length ? (
              <MEmpty icon="search" title={t('common.nothingFound')} />
            ) : (
              <MBlock>
                {curators.map((c, i) => {
                  const mySubtypes = c.curator_subtypes ?? [];
                  return (
                    <MRow
                      key={c.id}
                      to={`/admin/students/${c.id}`}
                      last={i === curators.length - 1}
                      leading={<Avatar name={c.full_name} src={c.avatar_url} />}
                      title={
                        <span className="flex items-center gap-1.5" data-admin-curator-row>
                          <span className="truncate">{c.full_name}</span>
                          {c.status !== 'active' && <MTag tone="muted">{STATUS_LABEL[c.status] ?? c.status}</MTag>}
                        </span>
                      }
                      meta={[
                        t('adminCurators.studentsCount', { n: curatorStudentsLabel(c) }),
                        mySubtypes.length ? mySubtypes.join(', ') : t('adminSubtypes.none'),
                      ].join(' · ')}
                      metaAccent={!mySubtypes.length}
                    />
                  );
                })}
              </MBlock>
            )}
          </>
        )}
        {modal}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('adminCurators.title')}
        subtitle={t('adminCurators.totalCount', { count: list.data?.total ?? 0 })}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">person_add</span>
            {t('adminStudents.add')}
          </Button>
        }
      />

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !all.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('adminCurators.empty')}{' '}
          <button type="button" onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('adminStudents.addFirst')}
          </button>
        </Card>
      ) : (
        <>
        <SearchInput className="mb-2" value={q} placeholder={t('adminCurators.searchPlaceholder')} onSearch={setQ} />
        <p className="mb-3 text-xs text-on-surface-variant">
          {t('common.shownOf', { shown: curators.length, total: all.length })}
        </p>
        {!curators.length && <Card variant="subtle" className="text-on-surface-variant">{t('common.nothingFound')}</Card>}
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {curators.map((c) => {
            const mySubtypes = c.curator_subtypes ?? [];
            return (
              <li key={c.id}>
                {/* ⚠️ КАРТОЧКА — ССЫЛКА В ПРОФИЛЬ. До D40 другого входа в карточку
                    куратора не было вовсе, и подтипы, которые ставятся именно там,
                    оказались бы недостижимы мышкой — ровно так когда-то «пропали»
                    Speaking Rooms (сторож navReachable). */}
                <Link to={`/admin/students/${c.id}`}>
                  <Card interactive className="flex items-center gap-3">
                    <Avatar name={c.full_name} src={c.avatar_url} size="lg" />
                    <div className="flex-1">
                      <p className="font-semibold">{c.full_name}</p>
                      <p className="text-xs text-on-surface-variant">{c.email}</p>
                      <div className="mt-1 flex flex-wrap gap-2">
                        <Pill variant={c.status === 'active' ? 'success' : 'neutral'}>{STATUS_LABEL[c.status] ?? c.status}</Pill>
                        <Pill variant="primary">
                          {t('adminCurators.studentsCount', { n: curatorStudentsLabel(c) })}
                        </Pill>
                        {mySubtypes.length ? (
                          mySubtypes.map((s) => (
                            <Pill key={s} variant="neutral">{s}</Pill>
                          ))
                        ) : (
                          <Pill variant="warning">{t('adminSubtypes.none')}</Pill>
                        )}
                      </div>
                    </div>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
        </>
      )}

      {modal}
    </>
  );
}
